/**
 * Calling-consent UI for a lead, a bulk selection, and CSV import.
 * api is API.json(method, url, body): a plain object is sent as JSON.
 * It returns parsed JSON and throws Error with err.message, err.status, and err.data.
 * Props: ConsentPanel, BulkConsentModal, ImportConsentFields, ConsentStatusPill.
 */
import { formatDateTime, getWorkspaceTimeZone, isValidTimeZone, zoneLabel } from './datetime.js';

const { useEffect, useId, useRef, useState } = React;

const CONSENT_TYPES = [
  ['express_written', 'Prior express written consent'],
  ['express', 'Prior express consent (not enough for AI calls)'],
  ['callback_request', 'Asked for a callback'],
  ['inbound_inquiry', 'Called us first'],
  ['existing_relationship', 'Existing customer (not enough for AI calls)']
];

const CHANNELS = [
  ['voice', 'Voice calls'],
  ['sms', 'Text messages'],
  ['voice_sms', 'Calls and texts']
];

const SOURCES = [
  ['web_form', 'Web form'],
  ['paper_form', 'Paper form'],
  ['recorded_verbal', 'Recorded verbal consent'],
  ['import_attestation', 'Import attestation'],
  ['callback_request', 'Callback request'],
  ['inbound_call', 'Inbound call'],
  ['api', 'API'],
  ['other', 'Other']
];

const AGENT_SOURCES = [
  ['callback_request', 'Callback request'],
  ['recorded_verbal', 'Recorded verbal consent'],
  ['inbound_call', 'Inbound call'],
  ['other', 'Other']
];

const LINE_TYPES = {
  landline: 'Landline',
  mobile: 'Mobile',
  fixedVoip: 'Fixed VoIP',
  nonFixedVoip: 'Non-fixed VoIP',
  tollFree: 'Toll-free'
};

const REASON_TEXT = {
  platform_paused: 'AI calling is paused by Nightcoders',
  tenant_suspended: 'AI calling is suspended for your account',
  terms_not_accepted: 'Your admin needs to accept the updated terms',
  opted_out: 'This person asked not to be called or is on your do-not-call list',
  outside_calling_hours: 'Outside allowed calling hours for this number (8am–9pm their time; 8am–8pm in Florida)',
  frequency_cap: 'Already called 3 times in the last 24 hours',
  no_consent: 'No consent on file for this phone number',
  not_business_landline: 'Not a verified business landline',
  line_type_unknown: 'Line type not checked yet'
};

const TEMPORARY_REASONS = {
  outside_calling_hours: true,
  frequency_cap: true
};

const ATTESTATION = 'I confirm we have this consent on file for every selected contact and can produce it on request.';

function errorMessage(err, fallback) {
  const data = err && err.data;
  if (data && data.message) return String(data.message);
  if (data && data.detail) return String(data.detail);
  if (err && err.message) return String(err.message);
  return fallback;
}

function notify(toast, message, type) {
  if (typeof toast === 'function') toast(message, type || 'info');
}

function roleOf(user) {
  return String((user && user.role) || '').trim();
}

function isRecordAdmin(user) {
  const role = roleOf(user);
  return role === 'tenant_admin' || role === 'super_admin';
}

function canCheckLineType(user) {
  const role = roleOf(user);
  return role === 'tenant_admin' || role === 'supervisor' || role === 'super_admin';
}

function isReadOnly(user) {
  const role = roleOf(user);
  return role === 'viewer' || role === 'analyst';
}

function canRecord(user) {
  return !!roleOf(user) && !isReadOnly(user);
}

function sourceOptions(user) {
  return roleOf(user) === 'agent' ? AGENT_SOURCES : SOURCES;
}

function defaultSource(user) {
  return roleOf(user) === 'agent' ? 'callback_request' : 'web_form';
}

function pairLabel(pairs, code) {
  if (code == null || code === '') return '—';
  for (let i = 0; i < pairs.length; i += 1) {
    if (pairs[i][0] === code) return pairs[i][1];
  }
  return String(code);
}

function reasonList(reasons) {
  if (!Array.isArray(reasons)) return [];
  return reasons.map((code) => (code == null ? '' : String(code))).filter(Boolean);
}

function reasonText(code, markTemporary) {
  const key = code == null ? '' : String(code);
  const text = REASON_TEXT[key] || key;
  if (markTemporary && TEMPORARY_REASONS[key]) return text + ' (temporary)';
  return text;
}

function lineTypeText(value) {
  if (value == null || value === '') return 'Not checked';
  return LINE_TYPES[value] || String(value);
}

function safeZone(timeZone) {
  if (isValidTimeZone(timeZone)) return String(timeZone).trim();
  const workspace = getWorkspaceTimeZone();
  if (isValidTimeZone(workspace)) return workspace;
  return 'America/Los_Angeles';
}

function zoneParts(date, timeZone) {
  const map = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  let hour = Number(map.hour);
  if (hour === 24) hour = 0;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour,
    minute: Number(map.minute),
    second: Number(map.second)
  };
}

function zoneOffsetMs(utcMs, timeZone) {
  const parts = zoneParts(new Date(utcMs), timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - utcMs;
}

/** datetime-local wall time in the workspace zone, same conversion as dispositions.js. */
function wallTimeToUtc(localValue, timeZone) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(localValue || ''));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const zone = safeZone(timeZone);
  const wanted = Date.UTC(year, month - 1, day, hour, minute, 0);
  let utc = wanted;
  for (let i = 0; i < 3; i += 1) {
    const next = wanted - zoneOffsetMs(utc, zone);
    if (next === utc) break;
    utc = next;
  }
  return new Date(utc).toISOString();
}

function utcToWallInput(iso, timeZone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = zoneParts(date, safeZone(timeZone));
  const pad = (n) => String(n).padStart(2, '0');
  return parts.year + '-' + pad(parts.month) + '-' + pad(parts.day) + 'T' + pad(parts.hour) + ':' + pad(parts.minute);
}

function nowWall(timeZone) {
  return utcToWallInput(new Date().toISOString(), timeZone);
}

function parseCaptured(localValue, timeZone) {
  if (!String(localValue || '').trim()) return { error: 'Enter the date and time consent was captured.' };
  const iso = wallTimeToUtc(localValue, timeZone);
  if (!iso) return { error: 'Enter a valid date and time.' };
  if (Date.parse(iso) > Date.now() + 60000) return { error: 'Date captured cannot be in the future.' };
  return { iso };
}

function formatWhen(iso, timeZone) {
  if (!iso) return '';
  const text = formatDateTime(iso, { timeZone: safeZone(timeZone), style: 'full' });
  return text === '\u2014' ? '' : text;
}

function httpsUrl(value) {
  const text = String(value || '').trim();
  if (!text) return { ok: true, url: '' };
  let url;
  try {
    url = new URL(text);
  } catch (e) {
    return { ok: false, url: '' };
  }
  if (url.protocol !== 'https:' || !url.hostname) return { ok: false, url: '' };
  return { ok: true, url: url.href };
}

function evidenceHref(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  try {
    const url = new URL(text);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    return url.href;
  } catch (e) {
    return '';
  }
}

function leadApiPath(leadId, suffix) {
  return '/api/leads/' + encodeURIComponent(String(leadId)) + suffix;
}

function emptyAuth() {
  return { allowed: false, reasons: [], lineType: null };
}

function normalizeAuth(authorization) {
  const auth = authorization && typeof authorization === 'object' ? authorization : {};
  return {
    allowed: auth.allowed === true,
    reasons: reasonList(auth.reasons),
    lineType: auth.lineType == null || auth.lineType === '' ? null : auth.lineType
  };
}

function idList(leadIds) {
  if (!Array.isArray(leadIds)) return [];
  return leadIds.filter((id) => id != null && String(id) !== '');
}

function countOf(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function recordedBy(row) {
  if (!row) return '—';
  if (row.recorded_by_name) return String(row.recorded_by_name);
  if (row.recorded_by != null && row.recorded_by !== '') return 'User #' + row.recorded_by;
  return '—';
}

function blankCapture(user, timeZone) {
  return {
    consentType: 'express_written',
    channel: 'voice',
    source: defaultSource(user),
    capturedLocal: nowWall(timeZone),
    seller: '',
    evidence: '',
    evidenceUrl: '',
    attested: false
  };
}

function attestationPayload(fields, timeZone) {
  if (!fields || !fields.attested) return null;
  const text = String(fields.evidence || '').trim();
  if (!fields.consentType || !text) return null;
  const parsed = parseCaptured(fields.capturedLocal, timeZone);
  if (!parsed.iso) return null;
  return {
    consent_type: fields.consentType,
    captured_at: parsed.iso,
    evidence_text: text,
    seller_name: String(fields.seller || '').trim()
  };
}

function Field({ id, label, children, hint }) {
  return (
    <div className="consent-field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <span className="consent-help" id={id + '-hint'}>{hint}</span> : null}
    </div>
  );
}

function CaptureFields({ idPrefix, user, zone, mode, value, onChange, disabled, autoFocus }) {
  const sources = sourceOptions(user);
  const sourceValue = sources.some((row) => row[0] === value.source) ? value.source : sources[0][0];
  const dateHint = 'Required. Interpreted in ' + zoneLabel(zone) + ', then saved in UTC. Cannot be in the future.';
  const max = nowWall(zone);

  function set(key, next) {
    const patch = {};
    patch[key] = next;
    onChange(Object.assign({}, value, patch));
  }

  return (
    <div className="consent-fields">
      <Field id={idPrefix + '-type'} label="Consent type">
        <select
          id={idPrefix + '-type'}
          className="consent-select"
          value={value.consentType}
          disabled={disabled}
          autoFocus={!!autoFocus}
          onChange={(event) => set('consentType', event.target.value)}
        >
          {CONSENT_TYPES.map((row) => <option key={row[0]} value={row[0]}>{row[1]}</option>)}
        </select>
      </Field>
      {mode === 'lead' ? (
        <Field id={idPrefix + '-channel'} label="Channel">
          <select
            id={idPrefix + '-channel'}
            className="consent-select"
            value={value.channel}
            disabled={disabled}
            onChange={(event) => set('channel', event.target.value)}
          >
            {CHANNELS.map((row) => <option key={row[0]} value={row[0]}>{row[1]}</option>)}
          </select>
        </Field>
      ) : null}
      {mode === 'lead' ? (
        <Field id={idPrefix + '-source'} label="Source">
          <select
            id={idPrefix + '-source'}
            className="consent-select"
            value={sourceValue}
            disabled={disabled}
            onChange={(event) => set('source', event.target.value)}
          >
            {sources.map((row) => <option key={row[0]} value={row[0]}>{row[1]}</option>)}
          </select>
        </Field>
      ) : null}
      <Field id={idPrefix + '-when'} label="Date captured" hint={dateHint}>
        <input
          id={idPrefix + '-when'}
          className="consent-input"
          type="datetime-local"
          required
          max={max}
          value={value.capturedLocal}
          disabled={disabled}
          aria-describedby={idPrefix + '-when-hint'}
          onChange={(event) => set('capturedLocal', event.target.value)}
        />
      </Field>
      <Field id={idPrefix + '-seller'} label="Seller name">
        <input
          id={idPrefix + '-seller'}
          className="consent-input"
          type="text"
          maxLength={200}
          value={value.seller}
          disabled={disabled}
          autoComplete="off"
          onChange={(event) => set('seller', event.target.value)}
        />
      </Field>
      <Field
        id={idPrefix + '-evidence'}
        label={mode === 'lead' ? 'Evidence' : 'Evidence description'}
        hint={mode === 'lead' ? '' : 'Required. For example, Signed up on acme.com/quote form.'}
      >
        <textarea
          id={idPrefix + '-evidence'}
          className="consent-textarea"
          rows={3}
          maxLength={4000}
          required={mode !== 'lead'}
          value={value.evidence}
          disabled={disabled}
          placeholder={mode === 'lead' ? 'What the person agreed to' : 'Signed up on acme.com/quote form'}
          aria-describedby={mode === 'lead' ? undefined : idPrefix + '-evidence-hint'}
          onChange={(event) => set('evidence', event.target.value)}
        />
      </Field>
      {mode === 'lead' ? (
        <Field id={idPrefix + '-url'} label="Evidence link" hint="Optional. https only.">
          <input
            id={idPrefix + '-url'}
            className="consent-input"
            type="url"
            inputMode="url"
            maxLength={2000}
            placeholder="https://"
            value={value.evidenceUrl}
            disabled={disabled}
            aria-describedby={idPrefix + '-url-hint'}
            onChange={(event) => set('evidenceUrl', event.target.value)}
          />
        </Field>
      ) : null}
      {mode === 'attest' ? (
        <label className="consent-check">
          <input
            type="checkbox"
            checked={!!value.attested}
            disabled={disabled}
            onChange={(event) => set('attested', event.target.checked)}
          />
          <span>{ATTESTATION}</span>
        </label>
      ) : null}
    </div>
  );
}

function ConsentRecord({ row, zone, canRevoke, busy, onRevoke }) {
  const revoked = !!(row && row.revoked_at);
  const href = evidenceHref(row && row.evidence_url);
  const captured = formatWhen(row && row.captured_at, zone);
  const revokedWhen = formatWhen(row && row.revoked_at, zone);
  const seller = row && row.seller_name ? String(row.seller_name) : '—';
  const evidence = row && row.evidence_text ? String(row.evidence_text).trim() : '';
  return (
    <article className={'consent-record' + (revoked ? ' consent-record--revoked' : '')}>
      <header className="consent-record-head">
        <strong>{pairLabel(CONSENT_TYPES, row.consent_type)}</strong>
        <span>{pairLabel(CHANNELS, row.channel)}</span>
      </header>
      <dl className="consent-meta">
        <dt>Source</dt>
        <dd>{pairLabel(SOURCES, row.source)}</dd>
        <dt>Captured</dt>
        <dd>{captured ? <time dateTime={row.captured_at}>{captured}</time> : '—'}</dd>
        <dt>Seller</dt>
        <dd>{seller}</dd>
        <dt>Recorded by</dt>
        <dd>{recordedBy(row)}</dd>
      </dl>
      {evidence ? <p className="consent-evidence">{evidence}</p> : null}
      {href ? (
        <a className="consent-link" href={href} target="_blank" rel="noopener noreferrer">{href}</a>
      ) : null}
      {revoked ? (
        <p className="consent-revoked">
          Revoked{revokedWhen ? ' ' + revokedWhen : ''}{row.revoke_reason ? ': ' + row.revoke_reason : ''}
        </p>
      ) : null}
      {canRevoke && !revoked ? (
        <div className="consent-actions">
          <button type="button" className="consent-btn consent-btn--danger" disabled={busy} onClick={() => onRevoke(row)}>
            Revoke
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function ConsentStatusPill({ allowed, reasons }) {
  const callable = allowed === true;
  const lines = reasonList(reasons).map((code) => reasonText(code, true));
  const title = lines.length
    ? (callable ? 'Callable: ' : 'Blocked: ') + lines.join(' · ')
    : (callable ? 'Callable' : 'Blocked');
  return (
    <span
      className={'consent-pill ' + (callable ? 'consent-pill--ok' : 'consent-pill--blocked')}
      title={title}
      aria-label={title}
    >
      {callable ? 'Callable' : 'Blocked'}
    </span>
  );
}

export function ConsentPanel({ api, toast, user, leadId, timezone, onChanged }) {
  const zone = safeZone(timezone);
  const formId = useId();
  const readOnly = !canRecord(user);
  const [consents, setConsents] = useState([]);
  const [authorization, setAuthorization] = useState(emptyAuth);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [formError, setFormError] = useState('');
  const [fields, setFields] = useState(() => blankCapture(user, zone));
  const [lineCheckedAt, setLineCheckedAt] = useState('');
  const [tick, setTick] = useState(0);
  const leadRef = useRef(leadId);
  const opRef = useRef(0);
  leadRef.current = leadId;
  const hasLead = leadId != null && String(leadId) !== '';
  const leadKey = hasLead ? String(leadId) : '';
  const [seenLead, setSeenLead] = useState(leadKey);
  const busy = pending !== '';
  if (seenLead !== leadKey) {
    opRef.current += 1;
    setSeenLead(leadKey);
    setLoaded(false);
    setLoading(!!leadKey);
    setConsents([]);
    setAuthorization(emptyAuth());
    setError('');
    setShowAdd(false);
    setFormError('');
    setLineCheckedAt('');
    setPending('');
    setFields(blankCapture(user, zone));
  }

  function applyPayload(data) {
    setConsents(Array.isArray(data && data.consents) ? data.consents : []);
    setAuthorization(normalizeAuth(data && data.authorization));
  }

  async function refresh(id) {
    const data = await api('GET', leadApiPath(id, '/consents'));
    if (String(leadRef.current) !== String(id)) return null;
    applyPayload(data);
    setLoaded(true);
    setError('');
    return data;
  }

  useEffect(() => {
    if (!hasLead) {
      setLoading(false);
      setLoaded(false);
      setConsents([]);
      setAuthorization(emptyAuth());
      setError('');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoaded(false);
    setError('');
    setShowAdd(false);
    setFormError('');
    setLineCheckedAt('');
    setFields(blankCapture(user, zone));
    api('GET', leadApiPath(leadId, '/consents'))
      .then((data) => {
        if (cancelled) return;
        applyPayload(data);
        setLoaded(true);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Could not load consent.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [api, leadId, hasLead, tick]);

  async function afterChange(id) {
    try {
      await refresh(id);
    } catch (err) {
      setError(errorMessage(err, 'Could not reload consent.'));
    }
    if (typeof onChanged === 'function') onChanged();
  }

  async function checkLineType() {
    if (!hasLead || pending || !canCheckLineType(user)) return;
    const id = leadId;
    const op = opRef.current + 1;
    opRef.current = op;
    setPending('check');
    try {
      const data = await api('POST', leadApiPath(id, '/line-type/check'));
      if (opRef.current !== op || String(leadRef.current) !== String(id)) return;
      const nextType = data && Object.prototype.hasOwnProperty.call(data, 'lineType') ? data.lineType : null;
      setAuthorization((prev) => Object.assign({}, prev, { lineType: nextType == null || nextType === '' ? null : nextType }));
      setLineCheckedAt(data && data.checkedAt ? data.checkedAt : new Date().toISOString());
      notify(toast, 'Line type checked: ' + lineTypeText(nextType), 'success');
      await afterChange(id);
    } catch (err) {
      if (opRef.current === op) notify(toast, errorMessage(err, 'Could not check the line type.'), 'error');
    } finally {
      if (opRef.current === op) setPending('');
    }
  }

  function askRevokeReason() {
    if (typeof window === 'undefined' || typeof window.prompt !== 'function') return '';
    const entered = window.prompt('Why is this consent being revoked?');
    if (entered == null) return null;
    return String(entered).trim();
  }

  async function revoke(row) {
    if (!hasLead || busy || readOnly || !row || row.revoked_at) return;
    const reason = askRevokeReason();
    if (reason == null) return;
    if (!reason) {
      notify(toast, 'Enter a reason to revoke this consent.', 'error');
      return;
    }
    const id = leadId;
    const op = opRef.current + 1;
    opRef.current = op;
    setPending('revoke');
    try {
      await api('POST', leadApiPath(id, '/consents/' + encodeURIComponent(String(row.id)) + '/revoke'), { reason });
      if (opRef.current !== op || String(leadRef.current) !== String(id)) return;
      notify(toast, 'Consent revoked.', 'success');
      await afterChange(id);
    } catch (err) {
      if (opRef.current === op) notify(toast, errorMessage(err, 'Could not revoke this consent.'), 'error');
    } finally {
      if (opRef.current === op) setPending('');
    }
  }

  async function addConsent(event) {
    event.preventDefault();
    if (!hasLead || busy || readOnly) return;
    const sources = sourceOptions(user);
    const source = sources.some((row) => row[0] === fields.source) ? fields.source : sources[0][0];
    const parsed = parseCaptured(fields.capturedLocal, zone);
    if (parsed.error) {
      setFormError(parsed.error);
      notify(toast, parsed.error, 'error');
      return;
    }
    const link = httpsUrl(fields.evidenceUrl);
    if (!link.ok) {
      const message = 'Evidence link must start with https://.';
      setFormError(message);
      notify(toast, message, 'error');
      return;
    }
    const id = leadId;
    const op = opRef.current + 1;
    opRef.current = op;
    setPending('save');
    setFormError('');
    try {
      await api('POST', leadApiPath(id, '/consents'), {
        consent_type: fields.consentType,
        channel: fields.channel || 'voice',
        source,
        captured_at: parsed.iso,
        seller_name: String(fields.seller || '').trim(),
        evidence_text: String(fields.evidence || '').trim(),
        evidence_url: link.url || null
      });
      if (opRef.current !== op || String(leadRef.current) !== String(id)) return;
      notify(toast, 'Consent recorded.', 'success');
      setShowAdd(false);
      setFields(blankCapture(user, zone));
      await afterChange(id);
    } catch (err) {
      if (opRef.current !== op) return;
      const message = errorMessage(err, 'Could not record consent.');
      setFormError(message);
      notify(toast, message, 'error');
    } finally {
      if (opRef.current === op) setPending('');
    }
  }

  const allowed = authorization.allowed === true;
  const reasons = reasonList(authorization.reasons);
  const temporary = reasons.some((code) => TEMPORARY_REASONS[code]);
  const ordered = consents.slice().sort((a, b) => {
    const left = Date.parse(a && a.captured_at) || 0;
    const right = Date.parse(b && b.captured_at) || 0;
    return right - left;
  });
  const checkedLabel = formatWhen(lineCheckedAt, zone);

  return (
    <section className="consent" aria-labelledby={formId + '-title'}>
      <h3 className="consent-title" id={formId + '-title'}>Calling consent</h3>
      {!hasLead ? <p className="consent-empty">Select a lead to see calling consent.</p> : null}
      {hasLead && error ? (
        <div className="consent-error" role="alert">
          <p>{error}</p>
          <button type="button" className="consent-btn" onClick={() => setTick((n) => n + 1)}>Try again</button>
        </div>
      ) : null}
      {hasLead && loading && !loaded ? <p className="consent-help">Loading consent…</p> : null}
      {hasLead && loaded ? (
        <div className="consent-body">
          <div className={'consent-status ' + (allowed ? 'consent-status--ok' : 'consent-status--blocked')} role="status">
            <p className="consent-status-line">{allowed ? 'AI can call this number' : 'AI calls blocked'}</p>
            {reasons.length ? (
              <ul className="consent-reasons" aria-label={allowed ? 'Authorization notes' : 'Why AI calls are blocked'}>
                {reasons.map((code, index) => (
                  <li key={code + '-' + index}>
                    {reasonText(code, false)}
                    {TEMPORARY_REASONS[code] ? <span className="consent-flag"> Temporary</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {temporary ? (
              <p className="consent-note">Calling hours and the daily call limit are temporary. They clear on their own.</p>
            ) : null}
          </div>
          <div className="consent-line">
            <span>Line type: <strong>{lineTypeText(authorization.lineType)}</strong></span>
            {checkedLabel ? <span className="consent-help">Checked <time dateTime={lineCheckedAt}>{checkedLabel}</time></span> : null}
            {canCheckLineType(user) ? (
              <button type="button" className="consent-btn" disabled={busy} onClick={checkLineType}>
                {pending === 'check' ? 'Checking…' : 'Check line type'}
              </button>
            ) : null}
          </div>
          {ordered.length ? (
            <div className="consent-records">
              {ordered.map((row, index) => (
                <ConsentRecord
                  key={row && row.id != null ? String(row.id) : 'row-' + index}
                  row={row}
                  zone={zone}
                  canRevoke={!readOnly}
                  busy={busy}
                  onRevoke={revoke}
                />
              ))}
            </div>
          ) : <p className="consent-empty">No consent records yet.</p>}
          {isReadOnly(user) ? <p className="consent-help">View only for your role.</p> : null}
          {readOnly ? null : (
            <div className="consent-actions">
              <button
                type="button"
                className="consent-btn"
                aria-expanded={showAdd}
                disabled={busy}
                onClick={() => {
                  setFormError('');
                  setShowAdd((open) => !open);
                }}
              >
                Add consent
              </button>
            </div>
          )}
          {!readOnly && showAdd ? (
            <form className="consent-form" onSubmit={addConsent}>
              <CaptureFields
                idPrefix={formId}
                user={user}
                zone={zone}
                mode="lead"
                value={fields}
                onChange={setFields}
                disabled={busy}
              />
              {formError ? <p className="consent-error" role="alert">{formError}</p> : null}
              <div className="consent-actions">
                <button type="submit" className="consent-btn consent-btn--primary" disabled={busy}>
                  {pending === 'save' ? 'Saving…' : 'Save consent'}
                </button>
                <button type="button" className="consent-btn" disabled={busy} onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function BulkConsentModal({ api, toast, user, leadIds, onClose, onDone }) {
  const zone = safeZone(undefined);
  const formId = useId();
  const titleId = formId + '-title';
  const ids = idList(leadIds);
  const [fields, setFields] = useState(() => blankCapture(user, zone));
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const admin = isRecordAdmin(user);

  useEffect(() => {
    if (!admin) return undefined;
    function onKey(event) {
      if (event.key === 'Escape' && !busy && typeof onClose === 'function') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [admin, busy, onClose]);

  if (!admin) return null;

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    if (!ids.length) {
      const message = 'Select at least one contact.';
      setFormError(message);
      notify(toast, message, 'error');
      return;
    }
    const parsed = parseCaptured(fields.capturedLocal, zone);
    if (parsed.error) {
      setFormError(parsed.error);
      notify(toast, parsed.error, 'error');
      return;
    }
    const evidence = String(fields.evidence || '').trim();
    if (!evidence) {
      const message = 'Describe the evidence you have on file.';
      setFormError(message);
      notify(toast, message, 'error');
      return;
    }
    if (!fields.attested) {
      const message = 'Confirm that you can produce this consent for every selected contact.';
      setFormError(message);
      notify(toast, message, 'error');
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const result = await api('POST', '/api/leads/consents/bulk', {
        lead_ids: ids,
        consent_type: fields.consentType,
        source: 'import_attestation',
        captured_at: parsed.iso,
        seller_name: String(fields.seller || '').trim(),
        evidence_text: evidence
      });
      const created = countOf(result && result.created);
      const skipped = countOf(result && result.skipped);
      notify(toast, 'Consent recorded for ' + created + ' contacts (' + skipped + ' skipped)', 'success');
      if (typeof onDone === 'function') onDone(result);
    } catch (err) {
      const message = errorMessage(err, 'Could not record consent.');
      setFormError(message);
      notify(toast, message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="consent-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy && typeof onClose === 'function') onClose();
      }}
    >
      <div className="consent-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="consent-modal-head">
          <h2 className="consent-title" id={titleId}>Record consent</h2>
          <button type="button" className="consent-btn" onClick={onClose} disabled={busy} aria-label="Close">Close</button>
        </div>
        <form onSubmit={submit}>
          <p className="consent-help">{ids.length} selected. Leads from Lead Finder are skipped.</p>
          <CaptureFields
            idPrefix={formId}
            user={user}
            zone={zone}
            mode="attest"
            value={fields}
            onChange={setFields}
            disabled={busy}
            autoFocus
          />
          {formError ? <p className="consent-error" role="alert">{formError}</p> : null}
          <div className="consent-actions consent-modal-foot">
            <button type="button" className="consent-btn" disabled={busy} onClick={onClose}>Cancel</button>
            <button type="submit" className="consent-btn consent-btn--primary" disabled={busy || !ids.length || !fields.attested}>
              {busy ? 'Saving…' : 'Record consent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ImportConsentFields({ value, onChange, user, timezone }) {
  const zone = safeZone(timezone);
  const formId = useId();
  const admin = isRecordAdmin(user);
  const [enabled, setEnabled] = useState(() => !!(value && value.consent_type && value.captured_at && String(value.evidence_text || '').trim()));
  const [fields, setFields] = useState(() => {
    const blank = blankCapture(user, zone);
    if (!value || typeof value !== 'object') return blank;
    return Object.assign({}, blank, {
      consentType: value.consent_type || blank.consentType,
      capturedLocal: value.captured_at ? utcToWallInput(value.captured_at, zone) : blank.capturedLocal,
      seller: value.seller_name || '',
      evidence: value.evidence_text || '',
      attested: !!(value.consent_type && value.captured_at && String(value.evidence_text || '').trim())
    });
  });
  const payload = enabled ? attestationPayload(fields, zone) : null;
  const payloadKey = JSON.stringify(payload);
  const lastKey = useRef(payloadKey);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const dateProblem = enabled ? parseCaptured(fields.capturedLocal, zone).error : '';

  useEffect(() => {
    if (!admin || typeof onChange !== 'function') return undefined;
    if (lastKey.current === payloadKey) return undefined;
    lastKey.current = payloadKey;
    onChange(payloadRef.current);
    return undefined;
  }, [admin, onChange, payloadKey]);

  if (!admin) return null;

  return (
    <div className="consent-import">
      <label className="consent-check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        <span>These contacts gave consent to be called</span>
      </label>
      {enabled ? (
        <div className="consent-import-body">
          <p className="consent-help">Consent is sent with the import only after the date, evidence, and confirmation are complete.</p>
          <CaptureFields
            idPrefix={formId}
            user={user}
            zone={zone}
            mode="attest"
            value={fields}
            onChange={setFields}
            disabled={false}
          />
          {dateProblem ? <p className="consent-error" role="alert">{dateProblem}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
